import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Link from 'next/link';
import { canOpenPayment, canStartNewReservation, formatMoney } from '../../booking/model';
import type { useReservation } from '../../booking/useReservation';
import type { useBooking } from '../../booking/useBooking';

interface Props {
  reservation: ReturnType<typeof useReservation>;
  booking: ReturnType<typeof useBooking>;
  onReturnToBooking: () => void;
}

export function ReservationCheckout({ reservation, booking, onReturnToBooking }: Props) {
  const { t, i18n } = useTranslation();
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const termsPath = i18n.language.startsWith('pl') ? '/regulamin-i-polityka-prywatnosci' : '/regulamin';
  const { status } = reservation;
  const statusKey = status?.status === 'payment-completed' ? 'paid'
    : status?.status === 'payment-canceled' ? 'canceled'
    : status?.status === 'hold-expired' ? 'expired'
    : status?.status === 'payment-review' || (status && ['unknown', 'failed'].includes(status.paymentState)) ? 'review'
    : status && ['pending', 'payment-pending'].includes(status.status) ? 'pending' : 'unconfirmed';

  return (
    <section id="booking-checkout" className="reservation-checkout" aria-labelledby="checkout-heading">
      <h3 id="checkout-heading">{t('checkout.heading')}</h3>
      {reservation.errorCode && (
        <p className="booking-error" role="alert">
          {t(`checkout.errors.${reservation.errorCode}`, { defaultValue: t('checkout.errors.API_UNAVAILABLE') })}
        </p>
      )}
      {reservation.waitingSeconds > 0 && <p role="status">{t('checkout.retryWait', { count: reservation.waitingSeconds })}</p>}
      {reservation.locked ? (
        <div className="reservation-status" aria-live="polite">
          <p>{reservation.submitting ? t('checkout.saving')
            : status ? t(`checkout.status.${statusKey}`)
            : reservation.attempt && !reservation.credentials ? t('checkout.unknownAttempt') : t('checkout.status.unconfirmed')}</p>
          {status && (
            <>
              <p>{t('checkout.reservationNumber')}: <strong>{status.reservationId}</strong></p>
              <dl className="pricing-rate-list">
                <div><dt>{t('booking.total')}</dt><dd>{formatMoney(status.totalMinor, i18n.language)}</dd></div>
                <div><dt>{t('booking.deposit')}</dt><dd>{formatMoney(status.refundableDepositMinor, i18n.language)}</dd></div>
                <div><dt>{t('booking.payNow')}</dt><dd>{formatMoney(status.payableNowMinor, i18n.language)}</dd></div>
              </dl>
            </>
          )}
          {reservation.canRetryAttempt && (
            <button type="button" className="booking-button" disabled={reservation.submitting || reservation.waitingSeconds > 0}
              onClick={reservation.retryAttempt}>{t('checkout.retryAttempt')}</button>
          )}
          {reservation.credentials && (
            <button type="button" className="text-button" disabled={reservation.polling || reservation.waitingSeconds > 0}
              onClick={reservation.refresh}>{reservation.polling ? t('checkout.checking') : t('checkout.refreshStatus')}</button>
          )}
          {reservation.recoveryBlocked && reservation.errorCode !== 'MISSING_STATUS_TOKEN' && (
            <button type="button" className="text-button" onClick={reservation.refresh}>{t('booking.retry')}</button>
          )}
          {status && canOpenPayment(status) && (
            <button type="button" className="booking-button" onClick={reservation.resumePayment}>{t('checkout.resumePayment')}</button>
          )}
          {canStartNewReservation(status) && (
            <button type="button" className="booking-button" disabled={reservation.leaving}
              onClick={async () => { if (await reservation.newReservation()) onReturnToBooking(); }}>
              {t('checkout.newReservation')}
            </button>
          )}
          {reservation.canLeavePaymentReturn && (
            <button type="button" className="booking-button" disabled={reservation.leaving}
              onClick={async () => { if (await reservation.leavePaymentReturn()) onReturnToBooking(); }}>
              {t('checkout.backToBooking')}
            </button>
          )}
        </div>
      ) : (
        <form noValidate onSubmit={event => { event.preventDefault(); void reservation.submit(); }}>
          <fieldset disabled={reservation.submitting}>
            <legend className="sr-only">{t('checkout.heading')}</legend>
            <div className="checkout-fields">
              {(['name', 'email', 'phone'] as const).map(field => {
                const error = touched[field] && reservation.customerErrors[field];
                return (
                  <label className="booking-field" key={field} htmlFor={`customer-${field}`}>
                    <span>{t(`reservation.${field}`)}</span>
                    <input id={`customer-${field}`} name={field} required
                      type={field === 'email' ? 'email' : field === 'phone' ? 'tel' : 'text'}
                      autoComplete={field === 'name' ? 'name' : field === 'phone' ? 'tel' : 'email'}
                      maxLength={field === 'name' ? 120 : field === 'email' ? 254 : 25}
                      value={reservation.customer[field]} aria-invalid={!!error}
                      aria-describedby={error ? `customer-${field}-error` : undefined}
                      onBlur={() => setTouched(previous => ({ ...previous, [field]: true }))}
                      onChange={event => reservation.setCustomer(previous => ({ ...previous, [field]: event.target.value }))} />
                    {error && <span id={`customer-${field}-error`} className="field-validation">{t(`checkout.validation.${field}`)}</span>}
                  </label>
                );
              })}
            </div>
            <div className="checkout-confirmations">
              <label>
                <input type="checkbox" id="accept-price" required checked={reservation.quoteAccepted} disabled={!booking.quote}
                  onChange={event => reservation.setQuoteAccepted(event.target.checked)} />
                <span>{t('checkout.acceptPrice')}</span>
              </label>
              {(['terms', 'privacy', 'deposit', 'marketing'] as const).map(key => (
                <label key={key}>
                  <input type="checkbox" id={`accept-${key}`} required={key !== 'marketing'} checked={reservation.consents[key]}
                    onChange={event => reservation.setConsents(previous => ({ ...previous, [key]: event.target.checked }))} />
                  <span>{t(`checkout.confirmations.${key}`)}{' '}
                    {(key === 'terms' || key === 'privacy') && (
                      <Link href={`${termsPath}${key === 'privacy' ? '#rodo' : ''}`} shallow scroll={false}>
                        {t(key === 'terms' ? 'header.terms' : 'reservation.rodoLink')}
                      </Link>
                    )}
                  </span>
                </label>
              ))}
            </div>
            <p className="field-hint">{t('reservation.paymentMethodsInfo')}</p>
            <button id="reservation-submit" type="submit" className="booking-button" disabled={!reservation.canSubmit}>
              {t('checkout.submit')}
            </button>
          </fieldset>
        </form>
      )}
    </section>
  );
}
